"use client";

import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import { Menu, X } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import { Link, usePathname } from '@/i18n/navigation';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";
import { UserContext } from "@/context/UserContext";
import config from "@/config";
import logo from "@/app/icon.png";
import SimpleLanguageSelector from "@/components/common/SimpleLanguageSelector";
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { DesktopGeneratorMenus, MobileGeneratorMenus } from './HeaderGeneratorMenus';

// 移动端菜单组件
interface MobileMenuProps {
  isOpen: boolean;
  onCloseMenu: () => void;
  onLoginClick: () => void;
  user: any;
}



const MobileMenu: React.FC<MobileMenuProps> = ({
  isOpen,
  onCloseMenu,
  onLoginClick,
  user
}) => {
  const t = useTranslations('Header');

  // 处理ESC键关闭
  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseMenu();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onCloseMenu]);

  // 当菜单打开时禁止背景滚动
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center">
        {/* Backdrop - 背景遮罩 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onCloseMenu}
          className="fixed inset-0 bg-gradient-to-b from-black/80 to-black/60 backdrop-blur-md"
        />

        {/* Menu Content - 菜单内容 */}
        <motion.div
          id="mobile-menu"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{
            type: 'spring',
            duration: 0.5,
            bounce: 0.2,
          }}
          onClick={(e) => e.stopPropagation()}
          className="fixed inset-0 z-[10000] bg-gradient-to-b from-slate-900 via-black to-slate-900 backdrop-blur-2xl border-l border-green-400/20 shadow-2xl shadow-green-400/10"
        >
          {/* 背景装饰效果 */}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(34,197,94,0.1),transparent_50%)]" />
          <div className="absolute top-0 right-0 w-32 h-32 bg-green-400/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-400/5 rounded-full blur-2xl" />

          {/* 内容区域 */}
          <div className="relative h-full flex flex-col">
            {/* 顶部区域 - 关闭按钮和标题 */}
            <div className="flex items-center justify-between p-2 sm:p-3 border-b border-green-400/20 bg-black/20 backdrop-blur-sm">
              <div className="flex items-center space-x-2">
                <div className="w-7 h-7 sm:w-8 sm:h-8 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center shadow-lg">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16m-7 6h7" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">{t('toggleMenu')}</h2>
                </div>
              </div>

              {/* 关闭按钮 */}
              <button
                onClick={onCloseMenu}
                className="p-1.5 sm:p-2 bg-black/40 hover:bg-red-500/20 backdrop-blur-sm rounded-full transition-all duration-300 text-gray-300 hover:text-white border border-gray-600/30 hover:border-red-400/50 shadow-lg hover:shadow-red-500/20 touch-manipulation active:scale-95"
                aria-label="Close menu"
              >
                <X className="w-3.5 h-3.5 sm:w-4 sm:h-4" aria-hidden="true" />
              </button>
            </div>

            {/* 内容区域 - 可滚动 */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-2 sm:p-3 space-y-2">
                {/* 用户和语言切换区域 - 简化结构 */}
                <div className="grid grid-cols-2 gap-2 p-1 sm:p-2">
                  {/* 登录/用户按钮 */}
                  <React.Suspense fallback={
                    <div className="h-14 bg-black/40 rounded-xl animate-pulse border border-green-400/20" />
                  }>
                    <LazyButtonSignin
                      {...{
                        mobileStyle: true,
                        fullWidth: true,
                        className: "w-full h-14 bg-gradient-to-r from-green-500/20 to-green-600/20 hover:from-green-500/30 hover:to-green-600/30 text-gray-200 hover:text-white rounded-xl px-3 transition-all duration-300 border border-green-400/30 hover:border-green-400/50 shadow-lg hover:shadow-green-500/20 flex items-center justify-center text-sm font-medium touch-manipulation active:scale-95 mobile-menu-item will-change-transform min-h-[56px] max-h-[56px]",
                        onLoginClick: onLoginClick
                      } as any}
                    />
                  </React.Suspense>

                  {/* 语言选择器 */}
                  <SimpleLanguageSelector
                    variant="mobile"
                    className="w-full h-14"
                    showLabel={false}
                  />
                </div>

                {/* 导航链接区域 */}
                <div className="space-y-1">
                  <h3 className="text-sm font-medium text-white mb-1 px-2">{t('toggleMenu')}</h3>

                  {[
                    { href: "/", label: t('Home') || 'Home' },
                    { href: "/pricing", label: t('Pricing') || 'Pricing' },
                    { href: "/explore", label: t('Explore') || 'Explore' },
                    { href: "/history", label: t('My Creations') || 'My Creations' },
                    {
                      href: "https://discord.com/invite/kd5KQYznq6",
                      label: t('Discord') || 'Discord',
                      isExternal: true,
                      icon: <DiscordIcon />
                    },
                  ].map((link, index) => (
                    <React.Fragment key={link.href}>
                    <div
                      className="transform transition-all duration-300"
                      style={{
                        animationDelay: `${index * 50}ms`
                      }}
                    >
                      {link.isExternal ? (
                        <a
                          href={link.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={onCloseMenu}
                          className="group flex items-center justify-between px-3 sm:px-4 py-3 sm:py-4 text-sm font-medium text-gray-200 hover:text-white bg-black/30 hover:bg-green-500/20 rounded-xl transition-all duration-300 hover:scale-[1.02] border border-green-400/20 hover:border-green-400/40 shadow-lg hover:shadow-green-500/20 touch-manipulation active:scale-98 mobile-menu-item will-change-transform"
                          title={link.label}
                          aria-label={`${link.label} (opens in new window)`}
                        >
                          <span className="flex items-center gap-3">
                            {link.icon && (
                              <span className="group-hover:scale-110 transition-transform duration-300 text-green-400" aria-hidden="true">
                                {link.icon}
                              </span>
                            )}
                            <span>{link.label}</span>
                          </span>
                          <svg className="w-4 h-4 text-gray-400 group-hover:text-green-400 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      ) : (
                        <Link
                          href={link.href}
                          onClick={onCloseMenu}
                          className="group flex items-center justify-between px-3 sm:px-4 py-3 sm:py-4 text-sm font-medium text-gray-200 hover:text-white bg-black/30 hover:bg-green-500/20 rounded-xl transition-all duration-300 hover:scale-[1.02] border border-green-400/20 hover:border-green-400/40 shadow-lg hover:shadow-green-500/20 touch-manipulation active:scale-98 mobile-menu-item will-change-transform"
                          aria-label={link.label}
                        >
                          <span className="flex items-center gap-3">
                            {link.icon && (
                              <span className="group-hover:scale-110 transition-transform duration-300 text-green-400" aria-hidden="true">
                                {link.icon}
                              </span>
                            )}
                            <span>{link.label}</span>
                          </span>
                          <svg className="w-4 h-4 text-gray-400 group-hover:text-green-400 transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      )}
                    </div>
                    {index === 0 && <MobileGeneratorMenus onNavigate={onCloseMenu} />}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

// 类型定义
interface HeaderProps { }

interface NavigationLink {
  href: string;
  label: string;
  isExternal?: boolean;
  icon?: React.ReactNode;
  mobileIcon?: React.ReactNode;
  isHighlighted?: boolean;
  isHot?: boolean;
}

interface UserCreditsProps {
  credits: number;
  isMobile?: boolean;
}

// 错误边界组件 - 使用函数组件和hooks
const HeaderErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const handleError = (error: ErrorEvent) => {
      setHasError(true);
    };

    window.addEventListener('error', handleError);
    return () => window.removeEventListener('error', handleError);
  }, []);

  if (hasError) {
    return (
      <div className="container px-4 mx-auto sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <div className="flex-shrink-0">
            <a href="/" className="flex items-center">
              <span className="text-xl font-bold text-white">{config.appName}</span>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// 优化的导航链接组件
const SafeNavigationLink: React.FC<{
  href: string;
  children: React.ReactNode;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  [key: string]: any;
}> = React.memo(({ href, children, className, onClick, ...props }) => {
  const locale = useLocale();

  const handleClick = useCallback((e: React.MouseEvent) => {
    try {
      if (onClick) {
        onClick(e);
      }
    } catch (error) {
      e.preventDefault();
      const localePrefix = locale === 'en' ? '' : `/${locale}`;
      window.location.href = `${localePrefix}${href}`;
    }
  }, [href, onClick, locale]);

  return (
    <Link href={href} className={className} onClick={handleClick} {...props}>
      {children}
    </Link>
  );
});

SafeNavigationLink.displayName = 'SafeNavigationLink';

// 用户积分显示组件
const UserCredits: React.FC<UserCreditsProps> = React.memo(({ credits, isMobile = false }) => {
  if (isMobile) {
    return (
      <div className="flex items-center gap-2 px-2.5 py-1.5 bg-black/40 backdrop-blur-sm rounded-lg border border-green-400/30">
        <svg
          viewBox="0 0 24 24"
          className="w-4 h-4 text-green-400"
          fill="currentColor"
        >
          <circle cx="12" cy="12" r="10" fill="currentColor" />
          <circle cx="12" cy="12" r="7" fill="none" stroke="#92400e" strokeWidth="1" />
          <path d="M9 10h6M9 14h6M12 8v2M12 14v2" stroke="#92400e" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        <span className="text-sm font-semibold text-green-400 leading-none">
          {credits}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-black/30 rounded-lg border border-green-400/20">
      <svg
        viewBox="0 0 24 24"
        className="w-4 h-4 text-green-400"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
      <span className="text-sm font-medium text-green-400 leading-none">
        {credits}
      </span>
      <span className="text-xs text-gray-400 leading-none">credits</span>
    </div>
  );
});

UserCredits.displayName = 'UserCredits';

// Discord图标组件
const DiscordIcon: React.FC = React.memo(() => (
  <svg
    className="w-5 h-5"
    viewBox="0 0 24 24"
    fill="currentColor"
  >
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
));

DiscordIcon.displayName = 'DiscordIcon';

// 热标签组件
const HotBadge: React.FC = React.memo(() => (
  <span className="absolute -top-2 -right-2 px-1 py-0.5 text-[10px] font-medium bg-red-500/20 text-red-500 rounded-full animate-pulse shadow-[0_0_5px_rgba(239,68,68,0.3)]">
    Hot
  </span>
));

HotBadge.displayName = 'HotBadge';

// 懒加载的登录按钮组件
const LazyButtonSignin = React.lazy(() => import("@/components/login/ButtonSignin").then(module => ({ default: module.default })));
const LazyLoginModal = React.lazy(() => import("@/components/modals/LoginModal").then(module => ({ default: module.default })));

// 主Header内容组件
interface HeaderContentProps {
  isOpen: boolean;
  onMenuToggle: () => void;
  onCloseMenu: () => void;
  onLoginClick: () => void;
}

const HeaderContent: React.FC<HeaderContentProps> = ({
  isOpen,
  onMenuToggle,
  onCloseMenu,
  onLoginClick
}) => {
  const t = useTranslations('Header');
  const searchParams = useSearchParams();
  const { user } = useContext(UserContext);

  // 处理登录按钮点击
  const handleLoginClick = useCallback(() => {
    try {
      onLoginClick();
    } catch (error) {
      // 静默处理错误
    }
  }, [onLoginClick]);

  // 导航链接配置 - 使用useMemo优化
  const links = useMemo((): NavigationLink[] => [
    { href: "/", label: t('Home') || 'Home' },
    { href: "/pricing", label: t('Pricing') || 'Pricing' },
    { href: "/explore", label: t('Explore') || 'Explore' },
    { href: "/history", label: t('My Creations') || 'My Creations' },
  ], [t]);

  // 监听搜索参数变化，关闭菜单
  useEffect(() => {
    try {
      onCloseMenu();
    } catch (error) {
      // 静默处理错误
    }
  }, [searchParams, onCloseMenu]);

  // 键盘导航支持
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onCloseMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onCloseMenu]);

  // 渲染桌面端导航菜单
  const renderDesktopNavigation = () => (
    <NavigationMenu>
      <NavigationMenuList className="flex items-center gap-3 space-x-0 xl:gap-6">
        {links.map((link, index) => (
          <React.Fragment key={link.href}>
          <NavigationMenuItem>
            {link.isExternal ? (
              <a
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-300 hover:text-white transition-colors duration-200 font-medium text-sm xl:text-base relative flex items-center space-x-1 leading-none"
              >
                {link.icon && <span className="text-green-400">{link.icon}</span>}
                <span>{link.label}</span>
                {(link.isHighlighted || link.isHot) && <HotBadge />}
              </a>
            ) : (
              <SafeNavigationLink
                href={link.href}
                className="text-gray-300 hover:text-white transition-colors duration-200 font-medium text-sm xl:text-base relative leading-none"
              >
                {link.label}
                {(link.isHighlighted || link.isHot) && <HotBadge />}
              </SafeNavigationLink>
            )}
          </NavigationMenuItem>
          {index === 0 && <DesktopGeneratorMenus />}
          </React.Fragment>
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  );

  // 渲染桌面端右侧组件
  const renderDesktopRightSection = () => (
    <div className="flex items-center space-x-4">
      {user && (
        <UserCredits credits={user.left_credits || 0} />
      )}

      <React.Suspense fallback={
        <div className="w-20 h-9 bg-green-500/20 rounded-md animate-pulse" />
      }>
        <LazyButtonSignin
          {...{
            extraStyle: "inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_15px_rgba(37,99,235,0.5)]",
            onLoginClick: () => handleLoginClick()
          } as any}
        />
      </React.Suspense>

      <SimpleLanguageSelector variant="default" />
    </div>
  );

  // 渲染移动端右侧组件
  const renderMobileRightSection = () => (
    <div className="flex lg:hidden items-center space-x-2 sm:space-x-3">
      {user && (
        <UserCredits credits={user.left_credits || 0} isMobile />
      )}

      <div className="flex-shrink-0">
        <React.Suspense fallback={
          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-green-500/20 rounded-lg animate-pulse" />
        }>
          <LazyButtonSignin
            {...{
              extraStyle: "inline-flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 rounded-lg transition-all duration-300 shadow-lg shadow-green-500/25 touch-target",
              mobileStyle: true,
              avatarOnly: true,
              onLoginClick: () => handleLoginClick()
            } as any}
          />
        </React.Suspense>
      </div>
    </div>
  );



  return (
    <div className="flex items-center justify-between h-full">
      {/* Logo */}
      <div className="flex-shrink-0 flex items-center">
        <Link href="/" className="flex items-center space-x-2 group">
          <div className="relative w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 flex items-center justify-center">
            <Image
              src={logo}
              alt=""
              width={32}
              height={32}
              className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-110"
              priority
            />
          </div>
          <span className="text-sm sm:text-base lg:text-lg font-bold text-white group-hover:text-green-400 transition-colors duration-300 leading-none">
            {config.appName}
          </span>
        </Link>
      </div>

      {/* 桌面端导航 */}
      <div className="hidden lg:flex items-center justify-center space-x-6 flex-1">
        {renderDesktopNavigation()}
      </div>

      {/* 桌面端右侧区域 */}
      <div className="hidden lg:flex items-center space-x-4 flex-shrink-0">
        {renderDesktopRightSection()}
      </div>

      {/* 移动端右侧区域 */}
      <div className="flex lg:hidden items-center space-x-2 sm:space-x-3 flex-shrink-0">
        {renderMobileRightSection()}

        {/* 移动端菜单按钮 - 增强触摸体验 */}
        <button
          onClick={onMenuToggle}
          className="p-2 sm:p-3 text-gray-300 hover:text-white transition-all duration-200 touch-manipulation active:scale-95 bg-black/20 hover:bg-green-500/20 rounded-lg border border-gray-600/30 hover:border-green-400/50 shadow-lg hover:shadow-green-500/20"
          aria-label={isOpen ? "Close menu" : "Open menu"}
          aria-expanded={isOpen}
          aria-controls="mobile-menu"
          aria-haspopup="true"
        >
          {isOpen ? (
            <X className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" />
          ) : (
            <Menu className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
};

// 主Header组件
const HeaderOptimized: React.FC<HeaderProps> = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { user, fetchUserInfo } = useContext(UserContext);
  const pathname = usePathname();
  const headerRef = useRef<HTMLDivElement>(null);

  // 菜单状态管理
  const handleMenuToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleLoginClick = useCallback(() => {
    setShowLoginModal(true);
  }, []);

  return (
    <>
      <header
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-[60] bg-slate-950/95 backdrop-blur-sm border-b border-green-400/30 shadow-lg transition-all duration-300 ease-in-out flex items-center"
        style={{
          '--header-height': '4rem',
          '--header-height-mobile': '3.5rem',
          height: 'var(--header-height-mobile)',
          '--banner-top': 'var(--header-height-mobile)'
        } as React.CSSProperties}
      >
        <div className="container mx-auto px-3 sm:px-4 lg:px-6 w-full">
          <HeaderContent
            isOpen={isOpen}
            onMenuToggle={handleMenuToggle}
            onCloseMenu={closeMenu}
            onLoginClick={handleLoginClick}
          />
        </div>
      </header>

      {/* 移动端菜单 - 在header外部渲染，确保正确的层级 */}
      <MobileMenu
        isOpen={isOpen}
        onCloseMenu={closeMenu}
        onLoginClick={handleLoginClick}
        user={user}
      />

      {/* 登录模态框 */}
      {showLoginModal && (
        <React.Suspense fallback={null}>
          <LazyLoginModal
            isOpen={showLoginModal}
            onClose={() => setShowLoginModal(false)}
            redirectTo={pathname}
            onSuccess={() => {
              setShowLoginModal(false);
              fetchUserInfo();
            }}
          />
        </React.Suspense>
      )}
    </>
  );
};

export default HeaderOptimized;
